import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import Loader from "../../components/Loader";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialConverstionRateFilterData } from "../../utils/initialData";
import ConverstionRateFilter from "./ConverstionRateFilter";
import ConversionRateModal from "./ConverstionRateModal";

const ConversionRate = () => {
  const [config] = useAuth();
  const [loading, setLoading] = useState(false);
  const [overviewData, setOverviewData] = useState(null);

  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRateKey, setSelectedRateKey] = useState(null);
  const [selectedRateLabel, setSelectedRateLabel] = useState("");
  const [detailedRate, setDetailedRate] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Filter State
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData
      ? JSON.parse(localFilterData)
      : initialConverstionRateFilterData;
  });

  // Target Editing State
  const [editingCell, setEditingCell] = useState(null); // { side: 'seller'|'buyer', stageTransition: string }
  const [editValue, setEditValue] = useState("");

  const buildQueryParams = (filters = {}, stageTransition = null, side = null) => {
    const params = {};
    const { created_start_date, created_end_date, created_at, bds } = filters;

    if (created_start_date) params.start_date = created_start_date;
    if (created_end_date) params.end_date = created_end_date;
    if (created_at) {
      const timeframeMap = {
        "Year To Date": "ytd",
        "Quarter To Date": "qtd",
        "Month To Date": "mtd",
      };
      params.timeframe = timeframeMap[created_at] || "ytd";
    }
    if (bds !== undefined && bds !== "") {
      params.bds = bds;
    }

    if (stageTransition) params.stage_transition = stageTransition;
    if (side) params.side = side;

    return params;
  };

  const fetchConversionOverview = useCallback(async (filters) => {
    setLoading(true);
    const baseParams = buildQueryParams(filters);
    let queryString = "";

    Object.entries(baseParams).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        queryString += `${queryString ? "&" : ""}${key}=${encodeURIComponent(value)}`;
      }
    });

    const url = `${BASE_URL}/conversion-rate-overview${queryString ? "?" + queryString : ""}`;

    try {
      const res = await axios.get(url, config);
      setOverviewData(res.data?.data || null);
    } catch (err) {
      console.error("API Error:", err);
      setOverviewData(null);
    } finally {
      setLoading(false);
    }
  }, [config]);

  const fetchDetailedRate = useCallback(async (filters, stageTransition, side) => {
    setModalLoading(true);
    const baseParams = buildQueryParams(filters, stageTransition, side);
    let queryString = "";

    Object.entries(baseParams).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        queryString += `${queryString ? "&" : ""}${key}=${encodeURIComponent(value)}`;
      }
    });

    queryString += `${queryString ? "&" : ""}include_records=1&records_limit=100`;

    const url = `${BASE_URL}/conversion-rate-overview${queryString ? "?" + queryString : ""}`;

    try {
      const res = await axios.get(url, config);
      return res.data?.success ? res.data.data : res.data;
    } catch (err) {
      console.error("API Error:", err);
      return {};
    } finally {
      setModalLoading(false);
    }
  }, [config]);

  // Update Target API Call
  const updateTarget = useCallback(async (stageTransition, side, newTarget) => {
    if (!newTarget || isNaN(newTarget)) {
      setEditingCell(null);
      setEditValue("");
      return;
    }

    try {
      const payload = {
        stageTransition,
        side,
        target_conversion: Number(newTarget),
      };

      const res = await axios.post(`${BASE_URL}/conversion-rate-targets`, payload, config);

      if (res.data?.success) {
        fetchConversionOverview(filterData); // Refresh table
        setEditingCell(null);
        setEditValue("");
      } else {
        alert("Failed to update target");
      }
    } catch (err) {
      console.error("Update Target Error:", err);
      alert("Error updating target. Please try again.");
    }
  }, [config, fetchConversionOverview, filterData]);

  // Initial fetch
  useEffect(() => {
    fetchConversionOverview(filterData);
  }, []);

  const onSuccess = useCallback((filters) => {
    if (filters) {
      setFilterData(filters);
      fetchConversionOverview(filters);
    }
  }, [fetchConversionOverview]);

  useEffect(() => {
    localStorage.setItem("filterData", JSON.stringify(filterData));
  }, [filterData]);

  const getStatusClass = (status) => {
    switch (status) {
      case "Above Target": return "bg-green-100 text-green-700";
      case "At Target": return "bg-amber-100 text-amber-700";
      default: return "bg-red-100 text-red-700";
    }
  };

  const handleViewDetails = useCallback(async (stageTransition, label, side) => {
    setSelectedRateKey(stageTransition);
    setSelectedRateLabel(label);
    setModalOpen(true);
    setDetailedRate(null);

    const detailedData = await fetchDetailedRate(filterData, stageTransition, side);
    setDetailedRate(detailedData);
  }, [fetchDetailedRate, filterData]);

  // Start editing
  const startEditing = (side, stageTransition, currentValue) => {
    setEditingCell({ side, stageTransition });
    setEditValue(currentValue?.replace('%', '').replace(/[^0-9.]/g, '') || "");
  };

  // Handle keyboard events
  const handleKeyDown = (e, stageTransition, side) => {
    if (e.key === "Enter") {
      updateTarget(stageTransition, side, editValue);
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setEditValue("");
    }
  };

  // Data extraction
  const biggestGap = overviewData?.summary?.biggest_gap || {};
  const belowTarget = overviewData?.summary?.below_target_stages || {};
  const atOrAboveTarget = overviewData?.summary?.at_or_above_target_stages || {};
  const sellSideFunnel = overviewData?.sell_side?.funnel || [];
  const sellSideSummary = overviewData?.sell_side?.summary || [];
  const buySideFunnel = overviewData?.buy_side?.funnel || [];
  const buySideSummary = overviewData?.buy_side?.summary || [];

  return (
    <BaseLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="head-1 dark-H">Conversion Rates</p>

          <ConverstionRateFilter
            filterData={filterData}
            onSetFilterData={setFilterData}
            from="converstionrate"
            onCallApiAgain={onSuccess}
          />
        </div>

        {loading && <Loader />}

        {/* Top Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <p className="body-N text-gray-600">BIGGEST GAP</p>
            <h2 className="head-2 mt-1 text-slate-900">
              {biggestGap.stage_transition || "N/A"}
            </h2>
            <p className="text-red-600 font-semibold text-lg mt-1">
              {biggestGap.gap_points ? `${biggestGap.gap_points}pp` : ""} {biggestGap.gap_label || ""}
            </p>
            <p className="body-S text-gray-500 mt-1">
              Current: {biggestGap.current_conversion || "0%"}
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
            <p className="body-N text-gray-600">BELOW TARGET STAGES</p>
            <h2 className="head-2 dark-H mt-2">{belowTarget.count ?? 0}</h2>
            <p className="body-S text-gray-500 mt-1">out of {belowTarget.total ?? 9}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
            <p className="body-N text-gray-600">AT OR ABOVE TARGET</p>
            <h2 className="head-2 dark-H mt-2">{atOrAboveTarget.count ?? 0}</h2>
            <p className="body-S text-gray-500 mt-1">out of {atOrAboveTarget.total ?? 9} stages</p>
          </div>
        </div>

        {/* Sell Side & Buy Side */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {/* Sell Side */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="head-2 dark-H">Sell Side Funnel</h3>
                <p className="body-N">Based on milestone achievement dates</p>
              </div>
            </div>

            <div className="flex items-center gap-3 overflow-x-auto pb-4">
              {sellSideFunnel.map((item, index) => (
                <React.Fragment key={index}>
                  <div className="text-center min-w-[100px]">
                    <div className="body-N mb-1">{item.label || `Stage ${index}`}</div>
                    <div className="head-2 dark-H">{item.count || 0}</div>
                  </div>
                  {index !== sellSideFunnel.length - 1 && (
                    <div className="text-3xl text-slate-300 pt-6">→</div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="bg-white rounded-xl border h-[400px] overflow-auto scrollbar-thin mt-6">
              <table className="w-full min-w-[800px] border-collapse">
                <thead className="sticky top-0 z-20 bg-[#d1dbcf]">
                  <tr className="text-left uppercase body-N dark-M">
                    <th className="p-4">Stage</th>
                    <th className="p-4">Rate</th>
                    <th className="p-4">Target</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="body-N dark-M">
                  {sellSideSummary.map((row, i) => (
                    <tr key={i} className="border-t hover:bg-gray-50">
                      <td className="p-4">{row.stage_transition}</td>
                      <td className="p-4 font-medium">{row.current_conversion}</td>

                      <td className="p-4">
                        {editingCell?.side === "seller" &&
                          editingCell?.stageTransition === row.stage_transition ? (
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, row.stage_transition, "seller")}
                            onBlur={() => updateTarget(row.stage_transition, "seller", editValue)}
                            autoFocus
                            className="w-20 px-3 py-1 border border-blue-500 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <div
                            className="flex items-center cursor-pointer group"
                            onClick={() => startEditing("seller", row.stage_transition, row.target_conversion)}
                          >
                            <span className="font-medium group-hover:text-blue-600 transition-colors">
                              {row.target_conversion}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-4 h-3 text-slate-400 group-hover:text-blue-500 transition-colors"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                          </div>
                        )}
                      </td>

                      <td className="p-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(row.status)}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          className="action-button view-details-btn px-4 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
                          onClick={() => handleViewDetails(row.stage_transition, row.stage_transition, "seller")}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Buy Side */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="head-2 dark-H">Buy Side Funnel</h3>
                <p className="body-N">Based on milestone achievement dates</p>
              </div>
            </div>

            <div className="flex items-center gap-3 overflow-x-auto pb-4">
              {buySideFunnel.map((item, index) => (
                <React.Fragment key={index}>
                  <div className="text-center min-w-[100px]">
                    <div className="body-N mb-1">{item.label}</div>
                    <div className="head-2 dark-H">{item.count}</div>
                  </div>
                  {index !== buySideFunnel.length - 1 && (
                    <div className="text-3xl text-slate-300 pt-6">→</div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="bg-white rounded-xl border h-[400px] overflow-auto scrollbar-thin mt-6">
              <table className="w-full min-w-[800px] border-collapse">
                <thead className="sticky top-0 z-20 bg-[#d1dbcf]">
                  <tr className="text-left uppercase body-N dark-M">
                    <th className="p-4">Stage</th>
                    <th className="p-4">Rate</th>
                    <th className="p-4">Target</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="body-N dark-M">
                  {buySideSummary.map((row, i) => (
                    <tr key={i} className="border-t hover:bg-gray-50">
                      <td className="p-4">{row.stage_transition}</td>
                      <td className="p-4 font-medium">{row.current_conversion}</td>

                      <td className="p-4">
                        {editingCell?.side === "buyer" &&
                          editingCell?.stageTransition === row.stage_transition ? (
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, row.stage_transition, "buyer")}
                            onBlur={() => updateTarget(row.stage_transition, "buyer", editValue)}
                            autoFocus
                            className="w-20 px-3 py-1 border border-blue-500 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <div
                            className="flex items-center cursor-pointer group"
                            onClick={() => startEditing("buyer", row.stage_transition, row.target_conversion)}
                          >
                            <span className="font-medium group-hover:text-blue-600 transition-colors">
                              {row.target_conversion}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-4 h-3 text-slate-400 group-hover:text-blue-500 transition-colors"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                          </div>
                        )}
                      </td>

                      <td className="p-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(row.status)}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          className="action-button view-details-btn px-4 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
                          onClick={() => handleViewDetails(row.stage_transition, row.stage_transition, "buyer")}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <ConversionRateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        rateKey={selectedRateKey}
        rateLabel={selectedRateLabel}
        detailedRate={detailedRate}
        loading={modalLoading}
      />
    </BaseLayout>
  );
};

export default ConversionRate;